import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { change, arrayPush, arrayPop } from 'redux-form';
import { get, find, omit } from 'lodash';
import moment from 'moment';
import { TimeMap } from '../elements-date-time/data/time-data';
import { DateTimeInput } from '../elements-date-time/date-time-input';
import Button from '../elements-buttons/Button';
import { FormattedMessage } from 'react-intl';
import classNames from 'classnames';

export default class DateTime extends Component {
  constructor (props) {
    super(props);

    this.getValueFromInput = this.getValueFromInput.bind(this);
    this.onTimeChange = this.onTimeChange.bind(this);
    this.onExtendClick = this.onExtendClick.bind(this);
    this.onRemoveClick = this.onRemoveClick.bind(this);
    this.extendDay = this.extendDay.bind(this);
    this.removeDay = this.removeDay.bind(this);
    this.validateBeforeFeeCalc = this.validateBeforeFeeCalc.bind(this);
  }

  onRemoveClick (e) {
    e.preventDefault();

    this.props.fields.pop();
    this.removeDay();
  }

  onExtendClick (e) {
    e.preventDefault();
    const { schedule } = this.props;
    const lastDateTime = schedule[schedule.length - 1];
    const lastStartTime = get(lastDateTime, 'start.time');
    const lastEndTime = get(lastDateTime, 'end.time');
    if (!lastStartTime || !lastEndTime) {
      return;
    }
    const date = lastDateTime.start.date;
    const day = moment(date.m + '-' + date.d + '-' + date.y, 'MM-DD-YYYY');
    const addDate = day.startOf('day').add(1, 'd').format('MMM, D, YYYY');

    this.props.fields.push({
      date: addDate,
      startTime: { ...lastDateTime.start.time },
      endTime: { ...lastDateTime.end.time }
    });

    this.extendDay();
  }

  async onTimeChange (stateKey, value, index) {
    const { dispatch, schedule, formName } = this.props;
    const timeMapValue = TimeMap[value];
    const date = schedule[index].start.date;
    const endDate = schedule[index].end.date;
    const day = moment(date.m + '-' + date.d + '-' + date.y, 'MM-DD-YYYY');
    const currentEndDate = moment(endDate.m + '-' + endDate.d + '-' + endDate.y, 'MM-DD-YYYY');

    const timeMapObj = {
      h: timeMapValue.h,
      m: timeMapValue.m
    };

    if ('capturedStartTime' === stateKey) {
      await dispatch(change(formName, `scheduleInput[${index}].startTime`, timeMapValue.time));
      await dispatch(change(formName, `schedule[${index}].start.time`, timeMapObj));

      return this.validateBeforeFeeCalc(index);
    }

    await dispatch(change(formName, `scheduleInput[${index}].endTime`, timeMapValue.time));
    await dispatch(change(formName, `schedule[${index}].end.time`, timeMapObj));

    if (timeMapValue.overnight) {
      const addDate = day.startOf('day').add(1, 'd').toObject();

      const newDate = {
        y: addDate.years,
        m: addDate.months + 1,
        d: addDate.date
      };

      await dispatch(change(formName, `schedule[${index}].end.date`, newDate));
    }

    if (currentEndDate > day) {
      await dispatch(change(formName, `schedule[${index}].end.date`, date));
    }

    return this.validateBeforeFeeCalc(index);
  }

  async getValueFromInput (index, dateString, newHash) {
    const { dispatch, schedule, formName } = this.props;
    const dateVal = moment(dateString).format('ll');
    const scheduleHash = schedule[index];
    const m = newHash.get('month') + 1;
    const y = newHash.get('year');
    const d = newHash.get('date');
    const day = moment(m + '-' + d + '-' + y, 'MM-DD-YYYY');

    const newDate = {
      y,
      m,
      d
    };

    const endTimeMap = find(TimeMap, { h: get(scheduleHash, 'end.time.h') }, null);
    const endDate = get(endTimeMap, 'overnight') ?
      day.startOf('day').add(1, 'd').toObject()
      :
      { years: y, months: m - 1, date: d };

    const endDateHash = {
      y: endDate.years,
      m: endDate.months + 1,
      d: endDate.date
    };

    await dispatch(change(formName, `scheduleInput[${index}].date`, dateVal));
    await dispatch(change(formName, `schedule[${index}].start.date`, newDate));
    await dispatch(change(formName, `schedule[${index}].end.date`, endDateHash));

    return this.validateBeforeFeeCalc(index);
  }

  async extendDay () {
    const { dispatch, schedule, getFeeCalc, formName } = this.props;
    const lastDateTime = schedule[schedule.length - 1];
    const date = lastDateTime.start.date;
    const day = moment(date.m + '-' + date.d + '-' + date.y, 'MM-DD-YYYY');
    const extendedDate = day.startOf('day').add(1, 'd').toObject();
    const endTimeMap = find(omit(TimeMap, 0), { h: lastDateTime.end.time.h, m: lastDateTime.end.time.m }, null);
    const endDate = endTimeMap.overnight ? day.startOf('day').add(1, 'd').toObject() : extendedDate;

    await dispatch(
      arrayPush(
        formName,
        'schedule',
        {
          start: {
            date: {
              y: extendedDate.years,
              m: extendedDate.months + 1,
              d: extendedDate.date
            },
            time: {
              ...lastDateTime.start.time
            }
          },
          end: {
            date: {
              y: endDate.years,
              m: endDate.months + 1,
              d: endDate.date
            },
            time: {
              ...lastDateTime.end.time
            }
          }
        }
      )
    );

    return getFeeCalc(null, false);
  }

  async removeDay () {
    const { dispatch, getFeeCalc, formName } = this.props;

    await dispatch(arrayPop(formName, 'schedule'));
    return getFeeCalc(null, false);
  }

  validateBeforeFeeCalc (index) {
    const { schedule, getFeeCalc } = this.props;

    if (schedule[index].start.time && schedule[index].end.time) {
      return getFeeCalc(null, true);
    }
  }
  render () {
    const { schedule, fields = [], reachedMaxDays, country } = this.props;

    const canExtendDay = (schedule || []).every((scheduleDay) => {
      const { start, end } = scheduleDay;
      const startValid = !!(start?.date && start?.time);
      const endValid = !!(end?.date && end?.time);
      return startValid && endValid;
    });

    return (
      <div className={classNames({ row: this.props.useBootstrapGrid })}>
        {
          fields.map((field, index) => {
            return (
              <DateTimeInput
                key={`${field}`}
                index={index}
                {...this.props}
                field={field}
                dateTimeHash={schedule[index]}
                onDateChange={this.getValueFromInput}
                onTimeChange={this.onTimeChange}
                isReduxForm
                country={country}
                useBootstrapGrid={this.props.useBootstrapGrid}
              />
            );
          })
        }

        <div className="schedule-length-control-group">
          {
            // Only allow up to 26 days
            !reachedMaxDays &&
            <Button
              className="text-small btn-link-secondary remove-click-styling"
              onClick={this.onExtendClick}
              type="button"
              attributes={{ 'data-testing-id': 'extend-a-day' }}
              disabled={!canExtendDay}
            >
              <FormattedMessage
                id="inbox_guest_update_booking.booking_details.extend_a_day"
                defaultMessage="Extend a day"
              />
            </Button>
          }

          {
            1 < fields.length ?
              <Button
                className="text-small btn-link remove-click-styling pull-right"
                onClick={this.onRemoveClick}
                type="button"
              >
                <FormattedMessage
                  id="inbox_guest_update_booking.booking_details.remove"
                  defaultMessage="Remove"
                />
              </Button>
              :
              null
          }
        </div>
      </div>
    );
  }
}

DateTime.defaultProps = {
  useBootstrapGrid: true
}

DateTime.propTypes = {
  fields: PropTypes.oneOfType([
    PropTypes.array,
    PropTypes.object
  ]),
  schedule: PropTypes.array,
  dispatch: PropTypes.func,
  formName: PropTypes.string,
  getFeeCalc: PropTypes.func,
  country: PropTypes.string,
  useBootstrapGrid: PropTypes.bool
};
